import FilterPanel from '@components/FilterPanel/FilterPanel';
import moment from 'moment';
import { Button, DatePicker, Form, Input, Radio, RadioChangeEvent, Select } from 'antd';
import { ChangeEvent, FunctionComponent, useCallback, useState } from 'react';
import css from './PromotionGamesFilter.module.less';
import {
  ArrowDownOutlined,
  PlusOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
} from '@ant-design/icons';
import localeWeightStore, { LOCALE, localeWeightMap, setLocaleWeight } from '@stores/localeWeight';
import useGuards from '@hooks/useGuards';
import useTranslate from '@hooks/useTranslate';
import componentContent from './PromotionGamesFilter.content.json';
import { GamesPromotionStatus } from '@interfaces/IPromotionGames';
import { useStore } from 'effector-react';
import promotion from '@stores/promotion';
import { RangePickerProps } from 'antd/lib/date-picker';
import { DATE_FORMAT } from '@config/format';
import { openModal } from '@stores/modals';

interface PromotionGamesFilter {}

const { Option } = Select;
const { RangePicker } = DatePicker;

const PromotionGamesFilter: FunctionComponent<PromotionGamesFilter> = () => {
  const { allowChange } = useGuards();
  const content = useTranslate(componentContent);
  const localeName = useStore(localeWeightStore);

  const { filters, setFilters, pagination, setPagination } = useStore(promotion);

  const [positionSortType] = useState<string>('desc');
  const [gameNameFilter, setGameNameFilter] = useState<string>('');
  const [gamePositionFilter, setGamePositionFilter] = useState<string>('');

  const createGamePromotion = useCallback(() => {
    openModal({ name: 'promotion' });
  }, []);

  const changeSortByDateHandler = useCallback(
    (sort: string) => {
      setFilters({ ...filters, sort: sort === 'STARTS_DESC' ? 'STARTS_ASC' : 'STARTS_DESC' });
    },
    [filters, setFilters],
  );

  const changeTypeFilter = (event: RadioChangeEvent) => {
    setFilters({ ...filters, statusFilter: event.target.value });
  };

  const onChangeDateRange = useCallback(
    (range: RangePickerProps['value']) => {
      const [dateStart, dateEnd] = range ?? [];
      let afterFilter, beforeFilter;

      if (dateStart) afterFilter = dateStart.startOf('day').format();
      if (dateEnd) beforeFilter = dateEnd.startOf('day').format();
      setFilters({
        ...filters,
        afterFilter,
        beforeFilter,
      });
    },
    [filters],
  );

  const onChangeLocale = useCallback(
    (localeFilter: LOCALE) => {
      setLocaleWeight(localeFilter);
      setFilters({ ...filters, localeFilter });
    },
    [filters],
  );

  const onGameFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
    setGameNameFilter(e.currentTarget.value);
  };

  const searchByGame = (gameNameFilter: string) => {
    setFilters({ ...filters, gameNameFilter: gameNameFilter });
  };

  const onPositionFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
    setGamePositionFilter(e.currentTarget.value);
  };

  const searchByPosition = (gamePositionFilter: string) => {
    setFilters({ ...filters, positionGTE: gamePositionFilter, positionLTE: gamePositionFilter });
  };

  const clearFilters = () => {
    setLocaleWeight(LOCALE.ALL);
    setPagination({ ...pagination, current: 1 });
    setFilters({});
    setGameNameFilter('');
    setGamePositionFilter('');
  };

  return (
    <Form layout='vertical'>
      <div className={css.filters}>
        <Form.Item className={css.headerButton}>
          {allowChange ? (
            <Button
              type='primary'
              icon={<PlusOutlined />}
              onClick={createGamePromotion}
              className={css.headerButton}
            >
              {content.add_promotion}
            </Button>
          ) : null}
          <Button
            type='primary'
            icon={<ArrowDownOutlined />}
            className={css.headerButton}
          >
            {content.statistics}
          </Button>
        </Form.Item>

        <FilterPanel
          actions={
            <Button
              htmlType='button'
              onClick={clearFilters}
            >
              {content.clear}
            </Button>
          }
        >
          <Form.Item label={content.choose_tab}>
            <Radio.Group
              onChange={changeTypeFilter}
              value={filters?.statusFilter}
              className={css.statusTabBar}
            >
              <Radio.Button value={GamesPromotionStatus.ACTIVE}>
                {content.active_status}
              </Radio.Button>
              <Radio.Button value={GamesPromotionStatus.PLANNED}>
                {content.planned_status}
              </Radio.Button>
              <Radio.Button value={GamesPromotionStatus.ENDED}>{content.ended_status}</Radio.Button>
              <Radio.Button value={GamesPromotionStatus.ERROR}>{content.all}</Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Form.Item label={content.choose_game}>
            <Input.Search
              allowClear
              className={css.filterInput}
              value={gameNameFilter}
              onSearch={searchByGame}
              onChange={onGameFilterChange}
            />
          </Form.Item>

          <Form.Item label={content.choose_position}>
            <Input.Search
              allowClear
              className={css.filterInput}
              value={gamePositionFilter}
              onSearch={searchByPosition}
              onChange={onPositionFilterChange}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type='link'
              style={{
                margin: 0,
                padding: 0,
              }}
            >
              <span>{content.sort_by_position}</span>
              {positionSortType === 'desc' ? <SortDescendingOutlined /> : <SortAscendingOutlined />}
            </Button>
          </Form.Item>

          <Form.Item label={content.choose_date}>
            <RangePicker
              className={css.dateRangeSelector}
              format={DATE_FORMAT}
              allowEmpty={[true, true]}
              value={[
                filters.afterFilter ? moment(filters.afterFilter) : null,
                filters.beforeFilter ? moment(filters.beforeFilter) : null,
              ]}
              placeholder={[content.rangePlaceholderFrom, content.rangePlaceholderTo]}
              onChange={onChangeDateRange}
            />
          </Form.Item>
          <Form.Item>
            <Button
              onClick={() => changeSortByDateHandler(filters.sort)}
              type='link'
              style={{
                margin: 0,
                padding: 0,
              }}
            >
              <span>{content.sort_by_date}</span>
              {filters.sort === 'STARTS_DESC' ? (
                <SortDescendingOutlined />
              ) : (
                <SortAscendingOutlined />
              )}
            </Button>
          </Form.Item>
          <Form.Item label={content.choose_locale}>
            <Select
              style={{ width: 120 }}
              value={localeName}
              onChange={onChangeLocale}
            >
              {localeWeightMap &&
                Object.entries(localeWeightMap).map(([key, value]) => {
                  return (
                    <Option
                      value={key}
                      key={key}
                    >
                      {value.name}
                    </Option>
                  );
                })}
            </Select>
          </Form.Item>
        </FilterPanel>
      </div>
    </Form>
  );
};

export default PromotionGamesFilter;
